import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { FilterType } from 'src/API';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { Patient, PatientList } from 'src/app/core/api/patient.service';
import { FilterService } from 'src/app/filter/filter.service';
import {
  buildAssessmentByPatientMap,
  buildQRByPatientMap,
  EnrichedPatient,
  enrichPatient
} from './patient-contact-priority.util';

const QR_FETCH_BATCH_SIZE = 10;

@Injectable()
export class PatientsPaginatorService {
  public allPatients: EnrichedPatient[] = [];
  private pageSize = 50;

  public get totalPatientsLoaded(): number {
    return this.allPatients.length;
  }

  public get hasMorePatientsToLoad(): boolean {
    return false;
  }

  constructor(
    private filterService: FilterService,
    private questionnaireRequestService: QuestionnaireRequestService
  ) {}

  public setPageSize(itemsPerPage: number) {
    this.pageSize = itemsPerPage;
  }

  public loadNextPatients(currentPage: number): Observable<PatientList> {
    const source$ =
      this.allPatients.length > 0
        ? of(this.allPatients)
        : forkJoin([
            this.filterService.queryFilterData(FilterType.patient, null),
            this.filterService.queryFilterData(FilterType.questionnaireRequest, null),
            this.filterService.queryFilterData(FilterType.assessment, null)
          ]).pipe(
            map(([patients, questionnaireRequests, assessments]: [Partial<Patient>[], any[], any[]]) => {
              const qrMap = buildQRByPatientMap(questionnaireRequests);
              const assessmentMap = buildAssessmentByPatientMap(assessments);
              return { patients: patients || [], qrMap, assessmentMap };
            }),
            switchMap(({ patients, qrMap, assessmentMap }) => {
              // Build reverse map: QR id → patient id for entries where type is not in filter data
              const qrIdToPatientId: { [id: string]: string } = {};
              const idsNeedingFetch: string[] = [];
              Object.keys(qrMap).forEach(patientId => {
                const questionnaireRequest = qrMap[patientId];
                if (questionnaireRequest && questionnaireRequest.id && !questionnaireRequest.type) {
                  qrIdToPatientId[questionnaireRequest.id] = patientId;
                  idsNeedingFetch.push(questionnaireRequest.id);
                }
              });

              if (idsNeedingFetch.length === 0) {
                return of({ patients, qrMap, assessmentMap });
              }

              // Fetch full QR records in batches to get type and config
              const batches: string[][] = [];
              for (let i = 0; i < idsNeedingFetch.length; i += QR_FETCH_BATCH_SIZE) {
                batches.push(idsNeedingFetch.slice(i, i + QR_FETCH_BATCH_SIZE));
              }

              return forkJoin(
                batches.map(batch =>
                  forkJoin(
                    batch.map(questionnaireRequestId =>
                      this.questionnaireRequestService.getQuestionnaireRequest(questionnaireRequestId)
                    )
                  )
                )
              ).pipe(
                map((batchResults: any[][]) => {
                  batchResults.forEach(batchGroup =>
                    batchGroup.forEach(fullQuestionnaireRequest => {
                      if (!fullQuestionnaireRequest) { return; }
                      const patientId = qrIdToPatientId[fullQuestionnaireRequest.id];
                      if (patientId) {
                        qrMap[patientId] = Object.assign({}, qrMap[patientId], {
                          type: fullQuestionnaireRequest.type,
                          config: fullQuestionnaireRequest.config
                        });
                      }
                    })
                  );
                  return { patients, qrMap, assessmentMap };
                })
              );
            }),
            map(({ patients, qrMap, assessmentMap }) =>
              patients.map(patient =>
                enrichPatient(patient, qrMap[patient.id] || null, assessmentMap[patient.id] || null)
              )
            ),
            tap((enriched: EnrichedPatient[]) => (this.allPatients = enriched))
          );

    return source$.pipe(map(list => this.sliceForPage(list, currentPage)));
  }

  private sliceForPage(list: EnrichedPatient[], currentPage: number): PatientList {
    const start = currentPage * this.pageSize;
    return {
      items: list.slice(start, start + this.pageSize) as Patient[],
      nextToken: null
    };
  }
}
